import { useState, useEffect } from 'react';
import { Users, AlertTriangle, TrendingUp, TrendingDown, Minus } from 'lucide-react';
import ModuleContainer from '../components/layout/ModuleContainer.jsx';
import PortfolioImpactPanel from '../components/PortfolioImpactPanel.jsx';
import TrackRecordPanel from '../components/TrackRecordPanel.jsx';
import { usePage } from '../contexts/PageContext.jsx';
import { usePageTracking } from '../hooks/usePageTracking.js';

const STANCE_COLORS = { bullish: '#10b981', bearish: '#ef4444', neutral: '#6b7280', mixed: '#f59e0b' };
const STANCE_ICONS = { bullish: TrendingUp, bearish: TrendingDown, neutral: Minus, mixed: Minus };

function StanceBadge({ stance }) {
  const color = STANCE_COLORS[stance] || '#6b7280';
  return <span className="text-[8px] font-mono font-bold uppercase px-1.5 py-0.5 rounded" style={{ color, backgroundColor: color + '15' }}>{stance}</span>;
}

export default function VoicesPage() {
  const { setPage } = usePage();
  useEffect(() => { setPage('voices'); }, []);

  const [voices, setVoices] = useState([]);
  const [consensus, setConsensus] = useState([]);
  const [divergences, setDivergences] = useState([]);
  const [sourceHealth, setSourceHealth] = useState([]);

  useEffect(() => {
    async function load() {
      const json = async (url) => { try { const r = await fetch(url); return r.ok ? await r.json() : null; } catch { return null; } };
      setVoices(await json('/api/voices') || []);
      setConsensus(await json('/api/voices/consensus') || []);
      setDivergences(await json('/api/voices/divergences') || []);
      setSourceHealth(await json('/api/sources/health') || []);
    }
    load();
    const iv = setInterval(load, 120000);
    return () => clearInterval(iv);
  }, []);

  const { impact, track, loading } = usePageTracking('voices');

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <Users className="w-6 h-6 text-korinth-accent" />
        <h1 className="text-2xl font-bold text-white">Voices Convergence</h1>
        <span className="text-korinth-muted text-sm font-mono">PYTHIA · Oracle — street consensus vs KAIROS stance</span>
      </div>

      {/* Sprint 20: Summary strip */}
      <div className="bg-korinth-bg/50 border border-korinth-border/30 rounded-lg px-4 py-2.5 text-[11px] font-mono text-korinth-text flex flex-wrap items-center gap-4">
        <span><span className="text-korinth-muted">Analysts tracked:</span> <span className="text-white font-bold">{voices.length}</span></span>
        <span><span className="text-korinth-muted">Active consensus themes:</span> <span className="text-korinth-accent font-bold">{consensus.length}</span></span>
        <span><span className="text-korinth-muted">KAIROS divergences:</span> <span className="text-korinth-warning font-bold">{divergences.length}</span></span>
      </div>

      {/* Sprint 20: Portfolio impact */}
      <PortfolioImpactPanel topic="Voices" {...impact} loading={loading} />

      {/* Divergence Alerts */}
      {divergences.length > 0 && (
        <ModuleContainer title="Divergence Alerts" subtitle="Where KAIROS disagrees with street consensus">
          <div className="space-y-2">
            {divergences.map(d => (
              <div key={d.asset} className="flex items-center gap-3 px-4 py-2 bg-korinth-danger/5 border border-korinth-danger/20 rounded-lg">
                <AlertTriangle className="w-4 h-4 text-korinth-warning flex-shrink-0" />
                <div className="flex-1">
                  <span className="text-xs font-mono font-bold text-white">{d.asset}</span>
                  <span className="text-[10px] text-korinth-muted ml-2">KAIROS: <span className={d.kairos_direction === 'buy' ? 'text-korinth-accent' : 'text-korinth-danger'}>{d.kairos_direction}</span></span>
                  <span className="text-[10px] text-korinth-muted ml-2">Street: <StanceBadge stance={d.consensus_stance} /></span>
                </div>
                <span className="text-[9px] font-mono text-korinth-warning">{d.divergence_from_kairos === 'contrary' ? 'CONTRARIAN' : 'PARTIAL'}</span>
              </div>
            ))}
          </div>
        </ModuleContainer>
      )}

      {/* Street Consensus Grid */}
      <ModuleContainer title="Street Consensus" subtitle={`${consensus.length} assets tracked`}>
        {consensus.length > 0 ? (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            {consensus.map(c => {
              const Icon = STANCE_ICONS[c.consensus_stance] || Minus;
              const color = STANCE_COLORS[c.consensus_stance] || '#6b7280';
              return (
                <div key={c.asset} className="bg-korinth-bg rounded-lg px-3 py-2 border border-korinth-border/30 text-center">
                  <div className="text-sm font-mono font-bold text-white">{c.asset}</div>
                  <Icon className="w-4 h-4 mx-auto my-1" style={{ color }} />
                  <StanceBadge stance={c.consensus_stance} />
                  <div className="flex justify-center gap-2 mt-1 text-[9px] font-mono text-korinth-muted">
                    <span className="text-korinth-accent">{c.bullish_count}B</span>
                    <span className="text-korinth-danger">{c.bearish_count}S</span>
                    <span>{c.neutral_count}N</span>
                  </div>
                  {c.divergence_from_kairos && c.divergence_from_kairos !== 'no_kairos' && (
                    <div className="mt-1">
                      <span className={`text-[7px] font-mono px-1 py-0.5 rounded ${c.divergence_from_kairos === 'contrary' ? 'bg-korinth-danger/10 text-korinth-danger' : c.divergence_from_kairos === 'aligned' ? 'bg-korinth-accent/10 text-korinth-accent' : 'bg-korinth-warning/10 text-korinth-warning'}`}>
                        vs KAIROS: {c.divergence_from_kairos}
                      </span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ) : <p className="text-[10px] text-korinth-muted font-mono text-center py-6">No consensus data yet — add voice statements via POST /api/voices/statement</p>}
      </ModuleContainer>

      {/* Tracked Voices */}
      <ModuleContainer title="Tracked Voices" subtitle={`${voices.length} analysts`}>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
          {voices.map(v => (
            <div key={v.id} className="flex items-center gap-2 px-3 py-2 bg-korinth-bg rounded border border-korinth-border/30">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5">
                  <span className="text-xs font-mono font-bold text-white truncate">{v.name}</span>
                  <StanceBadge stance={v.bias} />
                </div>
                <div className="text-[9px] text-korinth-muted font-mono truncate">{v.affiliation} | {v.specialty}</div>
              </div>
              <div className="text-right flex-shrink-0">
                <div className="text-sm font-mono font-bold" style={{ color: v.credibility_score >= 65 ? '#10b981' : v.credibility_score >= 45 ? '#f59e0b' : '#ef4444' }}>{v.credibility_score}</div>
                <div className="text-[7px] text-korinth-muted">cred</div>
              </div>
              <div className="text-right flex-shrink-0">
                <div className="text-xs font-mono text-korinth-text">{v.recent_statements || 0}</div>
                <div className="text-[7px] text-korinth-muted">14d</div>
              </div>
            </div>
          ))}
        </div>
      </ModuleContainer>

      {/* Source Health */}
      {sourceHealth.length > 0 && (
        <ModuleContainer title="Source Health" subtitle="All input sources with quality metrics">
          <div className="overflow-x-auto">
            <table className="w-full text-[10px] font-mono">
              <thead>
                <tr className="text-korinth-muted border-b border-korinth-border/30">
                  <th className="text-left py-1 px-2">Source</th>
                  <th className="text-left py-1 px-2">Type</th>
                  <th className="text-right py-1 px-2">Score</th>
                  <th className="text-right py-1 px-2">Hit Rate</th>
                  <th className="text-right py-1 px-2">Signals</th>
                  <th className="text-center py-1 px-2">Trend</th>
                  <th className="text-left py-1 px-2">Action</th>
                </tr>
              </thead>
              <tbody>
                {sourceHealth.map(s => (
                  <tr key={`${s.type}-${s.id}`} className="border-b border-korinth-border/10 hover:bg-korinth-bg/50">
                    <td className="py-1.5 px-2 text-white">{s.name || s.id}</td>
                    <td className="py-1.5 px-2">
                      <span className={`px-1 py-0.5 rounded ${s.type === 'voice' ? 'bg-blue-500/10 text-blue-400' : 'bg-purple-500/10 text-purple-400'}`}>{s.type}</span>
                    </td>
                    <td className="py-1.5 px-2 text-right font-bold" style={{ color: s.score >= 60 ? '#10b981' : s.score >= 40 ? '#f59e0b' : '#ef4444' }}>{s.score}</td>
                    <td className="py-1.5 px-2 text-right text-korinth-text">{s.hitRate != null ? `${s.hitRate.toFixed(0)}%` : '—'}</td>
                    <td className="py-1.5 px-2 text-right text-korinth-muted">{s.totalSignals}</td>
                    <td className="py-1.5 px-2 text-center">{s.trend === 'up' ? '↑' : s.trend === 'down' ? '↓' : '→'}</td>
                    <td className="py-1.5 px-2">
                      <span className={`text-[8px] px-1 py-0.5 rounded ${s.recommendation === 'increase' ? 'bg-green-500/10 text-green-400' : s.recommendation === 'decrease' || s.recommendation === 'flag_removal' ? 'bg-red-500/10 text-red-400' : 'bg-korinth-border text-korinth-muted'}`}>
                        {s.recommendation === 'insufficient_data' ? 'no data' : s.recommendation}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </ModuleContainer>
      )}

      {/* Sprint 20: Track record */}
      <TrackRecordPanel {...track} loading={loading} metric_name="Voices Consensus" />
    </div>
  );
}
